import { CampaignOutlined } from "@mui/icons-material";
import { Box } from "@mui/material";
import type { FC, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { InlineHint } from "../../../../../components/UIToolkit/molecules/InlineHint";
import { ReadOnlyMetadataField } from "../../../../../components/UIToolkit/molecules/ReadOnlyMetadataField";
import { SystemBanner } from "../../../../../components/UIToolkit/molecules/SystemBanner";
import { type StatusChecklistItem, SystemStatusChecklist } from "../../../../../components/UIToolkit/molecules/SystemStatusChecklist";
import type { ClipboardFacilityOption } from "./clipboardOnboardingTypes";
import { CLIPBOARD_STEP_COLUMNS_SX, CLIPBOARD_STEP_FORM_COLUMN_SX } from "./clipboardWizardLayout";

type Props = {
	departmentName: string;
	facility: ClipboardFacilityOption;
	/** Already-translated failure sentence from the last attempt, or null. */
	errorMessage: string | null;
	aside: ReactNode;
};

/**
 * Step 3 — the only step that writes anything, so it states exactly what it is about to do before it
 * does it. The order matters and is deliberate: the credential entry is written FIRST and the facility
 * id LAST, because "enrolled but unmapped" is a benign half-state the sync already handles, while
 * "mapped but unenrolled" hands support a live Post-to-Clipboard button backed by no token.
 */
export const ClipboardConfirmStep: FC<Props> = ({ departmentName, facility, errorMessage, aside }) => {
	const { t } = useTranslation();

	const willWrite: StatusChecklistItem[] = [
		{
			id: "credentials",
			label: t("department.config.clipboard.wizard.confirm.item.credentials.label"),
			description: t("department.config.clipboard.wizard.confirm.item.credentials.description"),
			state: "active",
			dataTestId: "clipboard-wizard-confirm-item-credentials",
		},
		{
			id: "facility",
			label: t("department.config.clipboard.wizard.confirm.item.facility.label"),
			description: t("department.config.clipboard.wizard.confirm.item.facility.description"),
			state: "active",
			dataTestId: "clipboard-wizard-confirm-item-facility",
		},
		{
			id: "roles",
			label: t("department.config.clipboard.wizard.confirm.item.roles.label"),
			description: t("department.config.clipboard.wizard.confirm.item.roles.description"),
			state: "pending",
			dataTestId: "clipboard-wizard-confirm-item-roles",
		},
	];

	return (
		<Box sx={CLIPBOARD_STEP_COLUMNS_SX}>
			<Box sx={CLIPBOARD_STEP_FORM_COLUMN_SX}>
				{/* Stacked, not wrapped side-by-side: the facility value is a name plus a 24-char workplace
				    id, so in this column two abreast would ellipsize both into uselessness. */}
				<Box sx={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
					<ReadOnlyMetadataField
						label={t("department.config.clipboard.wizard.confirm.field.department")}
						value={departmentName}
						dataTestId="clipboard-wizard-confirm-department"
					/>
					{/* Name and id are separate fields, not one `name · id` string: together they are far wider
					    than this column, so combining them ellipsized the id — the one value an operator
					    actually needs to read back against `list_facilities.py`. */}
					<ReadOnlyMetadataField
						label={t("department.config.clipboard.wizard.confirm.field.facility")}
						value={facility.name}
						dataTestId="clipboard-wizard-confirm-facility"
					/>
					<ReadOnlyMetadataField
						label={t("department.config.integrations.clipboard.facilityId.label")}
						value={facility.facilityId}
						copyable
						dataTestId="clipboard-wizard-confirm-facility-id"
					/>
				</Box>
				<SystemStatusChecklist
					title={t("department.config.clipboard.wizard.confirm.willWrite")}
					items={willWrite}
					dataTestId="clipboard-wizard-confirm-checklist"
				/>
				<SystemBanner
					tone="warning"
					icon={<CampaignOutlined />}
					title={t("department.config.clipboard.wizard.facility.warning.title")}
					description={t("department.config.clipboard.wizard.confirm.warning.description")}
					dataTestId="clipboard-wizard-confirm-warning"
				/>
				{errorMessage && (
					<InlineHint tone="warning" dataTestId="clipboard-wizard-confirm-error">
						{errorMessage}
					</InlineHint>
				)}
			</Box>
			{aside}
		</Box>
	);
};
